<template>
  <RevForm
    :initial-values
    :validate
    @submit="(values) => emit('submit', values)"
  >
    <template #default="{ values, errors }">
      <div class="flex gap-24">
        <div class="basis-1/2">
          <RevInputFileUpload
            id="evidence-upload-file-input"
            v-model="values.attachments"
            :accept="ACCEPTED_FILE_EXTENSIONS"
            :description="errors.attachments"
            :errors="errors.attachments as any"
            :label-empty="uploadLabel"
          />
        </div>
        <div class="basis-1/2">
          <slot />
        </div>
      </div>

      <div class="mt-24 space-x-4" :class="{ 'mb-8': isMessageSectionOpen }">
        <RevLink @click="() => (isMessageSectionOpen = !isMessageSectionOpen)">
          {{ i18n(translations.addDetailsLink) }}
        </RevLink>
        <span class="body-1">{{ i18n(translations.optional) }}</span>
      </div>

      <RevInputTextarea
        v-if="isMessageSectionOpen"
        id="evidence-upload-text-input"
        v-model="values.message"
        :error="errors.message"
        :label="i18n(translations.textareaLabel)"
        name="message"
      />

      <div class="relative mt-24 flex items-center gap-16">
        <NextTaskCheckbox
          v-if="hasNextTask"
          v-model="values.isNextTaskChecked"
        />

        <RevButton
          :disabled="isLoading"
          :loading="isLoading"
          type="submit"
          variant="primary"
        >
          {{ i18n(translations.submitButton) }}
        </RevButton>
      </div>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import {
  FORM_VALID,
  RevForm,
  type Validator,
  makeValidate,
  required,
} from '@ds/components/Form'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import { RevInputTextarea } from '@ds/components/InputTextarea'
import { RevLink } from '@ds/components/Link'

import type { OrderlineOpenTask } from '~/api/seller-after-sales/types'

import NextTaskCheckbox from '../../../../NextTaskCheckbox/NextTaskCheckbox.vue'

import translations from './UploadEvidence.translations'

const i18n = useI18n()

const { uploadLabel, mostUrgentTask } = defineProps<{
  uploadLabel: string
  isLoading: boolean
  mostUrgentTask?: OrderlineOpenTask
}>()

const emit = defineEmits(['submit'])

const hasNextTask = computed(() => Boolean(mostUrgentTask))

type Values = {
  attachments: File[]
  message: string
  isNextTaskChecked: boolean
}

const initialValues = reactive<Values>({
  attachments: [],
  message: '',
  isNextTaskChecked: hasNextTask.value,
})

const isMessageSectionOpen = ref(false)

function validFileSize(errorMessage: string) {
  return function validate(uploadedFiles: File[]) {
    const totalSize = uploadedFiles.reduce((size, file) => size + file.size, 0)

    return totalSize > 10000000 ? errorMessage : FORM_VALID
  }
}

const validate = makeValidate<Values>({
  attachments: [
    required(i18n(translations.requiredMessageError)),
    validFileSize(
      i18n(translations.fileInputMaxSizeError),
    ) as Validator<Values>,
  ],
})

const ACCEPTED_FILE_EXTENSIONS = [
  // PNG
  '.png',
  'image/png',

  // JPG
  '.jpg',
  'image/jpg',
  '.jpeg',
  'image/jpeg',

  // HEIF
  '.heic',
  'image/heic',
  '.heif',
  'image/heif',

  // PDF
  '.pdf',
  'application/pdf',

  // MP4
  '.mp4',
  'video/mp4',
  'audio/mp4',
  'application/mp4',

  // ZIP
  '.zip',
  'application/zip',
]
</script>
